import { App, Button, Form, Space } from 'antd';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { hrApi } from '@/api/hr';
import { PageHeader } from '@/shared/components/page-header';
import { extractError } from '@/shared/lib/error-message';
import { usePhotoUpload } from '@/shared/lib/use-photo-upload';
import { StaffForm, valuesToInput, type StaffFormShape } from './staff-form';

export function HrStaffNewPage() {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [form] = Form.useForm<StaffFormShape>();
  const photo = usePhotoUpload('staff-photos');

  const create = useMutation({
    mutationFn: (values: StaffFormShape) => hrApi.createStaff(valuesToInput(values)),
    onSuccess: (created) => {
      message.success(`Created ${created.firstName} ${created.lastName}`);
      navigate(`/hr/staff/${created.id}`, { replace: true });
    },
    onError: (e) => message.error(extractError(e)),
  });

  const submit = async (values: StaffFormShape) => {
    try {
      const uploaded = await photo.commit();
      if (uploaded !== undefined) values = { ...values, photoPath: uploaded };
    } catch (e) {
      message.error(extractError(e, 'Photo upload failed'));
      return;
    }
    create.mutate(values);
  };

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module / Staff"
        title="New staff"
        description="Profile, employment, bank, and emergency contact. Qualifications and experience can be added on the staff detail page after creation."
        actions={
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/hr/staff')}>
              Back
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={create.isPending}
              onClick={() => form.submit()}
            >
              Save
            </Button>
          </Space>
        }
      />

      <StaffForm
        form={form}
        layout="cards"
        onFinish={submit}
        onPhotoPendingChange={photo.onPendingChange}
      />
    </div>
  );
}
